import { createRouter, createWebHistory } from 'vue-router'
import { authGuard } from '@auth0/auth0-vue'
import { FormCategory, FormProcessType } from '@/gql/graphql'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: (to, from, savedPosition) => {
    const anchorElement = to.hash ? document.querySelector(to.hash) : undefined
    if (to.hash && anchorElement) {
      return {
        el: to.hash,
        behavior: 'smooth',
        top: parseFloat(getComputedStyle(anchorElement).scrollMarginTop)
      }
    }
    if (savedPosition) return savedPosition
    if (history.state?.preserveScroll) return false

    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: { name: 'dashboard' },
      beforeEnter: authGuard,
      children: [
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue')
        },
        {
          path: 'guides',
          name: 'guides',
          component: () => import('@/views/GuidesView.vue')
        },
        {
          path: 'strategy',
          children: [
            {
              path: '',
              name: 'strategy',
              component: () => import('@/views/Strategy/StrategyView.vue'),
              props: (route) => {
                const getNumberList = (value: typeof route.query.scenario) =>
                  (Array.isArray(value) ? value : [value])
                    .flatMap((queryValue) => queryValue?.split(',') ?? [])
                    .map(Number)
                    .filter(
                      (value, index, values) =>
                        Number.isInteger(value) && values.indexOf(value) === index
                    )
                const getNumber = (value: typeof route.query.scenario) => {
                  const queryValue = Array.isArray(value) ? value[0] : value
                  const number = queryValue === undefined ? undefined : Number(queryValue)

                  return Number.isInteger(number) ? number : undefined
                }

                return {
                  scenarioId: getNumberList(route.query.scenario)[0],
                  strategyIds: getNumberList(route.query.strategies),
                  yearFrom: getNumber(route.query['year-from']),
                  yearTo: getNumber(route.query['year-to'])
                }
              }
            }
          ]
        },
        {
          path: 'design',
          name: 'design',
          component: () => import('@/views/Design/DesignView.vue')
        },
        {
          path: 'settings',
          name: 'settings',
          redirect: { name: 'billing' },
          component: () => import('@/views/Settings/SettingsView.vue'),
          children: [
            {
              path: 'billing',
              name: 'billing',
              component: () => import('@/views/Settings/BillingDetailsView.vue')
            },
            {
              path: 'audit-log',
              name: 'audit-log',
              component: () => import('@/views/Settings/AuditLogView.vue')
            },
            {
              path: 'users',
              name: 'users',
              redirect: { name: 'users-overview' },
              children: [
                {
                  path: 'overview',
                  name: 'users-overview',
                  component: () => import('@/views/Settings/UsersView.vue')
                },
                {
                  path: 'create',
                  name: 'create-user',
                  component: () => import('@/views/Settings/CreateUser.vue')
                }
              ]
            }
          ]
        },
        {
          path: 'assets',
          name: 'assets',
          component: () => import('@/views/Assets/AssetsOverviewView.vue')
        },
        {
          path: 'import-assets',
          name: 'import-assets',
          component: () => import('@/views/Assets/ImportAssetsView.vue')
        },
        {
          path: 'impact-accounts',
          component: () => import('@/views/ImpactAccounts/ImpactAccountsView.vue'),
          children: [
            {
              path: '',
              name: 'impact-accounts',
              component: () => import('@/views/ImpactAccounts/ImpactAccountPanel.vue')
            },
            {
              path: 'create',
              name: 'create-impact-account',
              component: () => import('@/views/ImpactAccounts/EditImpactAccountView.vue'),
              props: () => ({ id: undefined })
            },
            {
              path: ':id(\\d+)',
              children: [
                {
                  path: '',
                  name: 'impact-account',
                  component: () => import('@/views/ImpactAccounts/SingleImpactAccountView.vue'),
                  props: (route) => ({ id: +route.params.id })
                },
                {
                  path: 'edit',
                  name: 'edit-impact-account',
                  component: () => import('@/views/ImpactAccounts/EditImpactAccountView.vue'),
                  props: (route) => ({ id: +route.params.id })
                }
              ]
            }
          ]
        },
        {
          path: 'reporting',
          name: 'reporting',
          redirect: { name: 'csrd-report' },
          component: () => import('@/views/Reporting/ReportingView.vue'),
          children: [
            {
              path: 'projects',
              name: 'projects-report',
              redirect: { name: 'export-report' }
            },
            {
              path: 'removal-methods',
              name: 'removal-methods-report',
              redirect: { name: 'export-report' }
            },
            {
              path: 'impact-accounts',
              name: 'impact-accounts-report',
              redirect: { name: 'export-report' }
            },
            {
              path: 'export',
              name: 'export-report',
              component: () => import('@/views/Reporting/ReportExportData.vue')
            },
            {
              path: 'csrd',
              name: 'csrd-report',
              component: () => import('@/views/Reporting/ReportByCsrd.vue')
            },
            {
              path: 'cdp',
              name: 'cdp-report',
              component: () => import('@/views/Reporting/ReportByCdp.vue')
            }
          ]
        },
        {
          path: 'new-order',
          children: [
            {
              path: '',
              name: 'new-order',
              component: () => import('@/views/NewOrder/NewOrderView.vue')
            },
            {
              path: 'single',
              children: [
                {
                  path: '',
                  name: 'order-single-projects',
                  component: () => import('@/views/NewOrder/Single/OrderSingleProjectCatalog.vue')
                },
                {
                  path: ':id(-?\\d+)/:vintageYear(\\d{4})',
                  children: [
                    {
                      path: '',
                      name: 'order-single-project',
                      component: () => import('@/views/NewOrder/Single/OrderSingleProjectView.vue'),
                      props: (route) => ({
                        id: +route.params.id,
                        vintageYear: +route.params.vintageYear
                      })
                    },
                    {
                      path: 'checkout',
                      name: 'project-checkout',
                      component: () =>
                        import('@/views/NewOrder/Single/OrderProjectCheckoutView.vue'),
                      props: (route) => ({
                        id: +route.params.id,
                        vintageYear: +route.params.vintageYear,
                        quantity:
                          typeof route.query.quantity === 'string' && +route.query.quantity > 0
                            ? +route.query.quantity
                            : undefined
                      })
                    }
                  ]
                }
              ]
            },
            {
              path: 'portfolio',
              children: [
                {
                  path: '',
                  name: 'portfolios',
                  component: () => import('@/views/NewOrder/Portfolio/PortfolioCatalog.vue'),
                  props: (route) => {
                    const id = route.query['id']
                    const hasId = typeof id !== 'object' && /^\d+$/.test(id)
                    if (hasId) {
                      return { id: +id }
                    }
                    return {}
                  }
                },
                {
                  path: ':id(-?\\d+)',
                  children: [
                    {
                      path: '',
                      name: 'portfolio',
                      component: () => import('@/views/NewOrder/Portfolio/PortfolioView.vue'),
                      props: (route) => ({
                        id: +route.params.id
                      })
                    },
                    {
                      path: 'checkout',
                      name: 'portfolio-checkout',
                      component: () =>
                        import('@/views/NewOrder/Portfolio/PortfolioOrderCheckoutView.vue'),
                      props: (route) => ({
                        id: +route.params.id,
                        quantity:
                          typeof route.query.quantity === 'string' && +route.query.quantity > 0
                            ? +route.query.quantity
                            : 0
                      })
                    }
                  ]
                }
              ]
            },
            {
              path: 'portfolio-generator',
              name: 'portfolio-generator',
              redirect: { name: 'portfolio-generator-index' },
              children: [
                {
                  path: '',
                  name: 'portfolio-generator-index',
                  component: () => import('@/views/NewOrder/PortfolioGenerator/Index.vue')
                },
                {
                  path: 'configuration/:id(\\-?\\d+)?',
                  name: 'portfolio-generator-edit-configuration',
                  component: () =>
                    import('@/views/NewOrder/PortfolioGenerator/EditConfiguration.vue'),
                  props: (route) => ({
                    id: !route.params.id ? undefined : +route.params.id,
                    tranche: !route.query.tranche ? undefined : +route.query.tranche
                  })
                },
                {
                  path: 'auto-create',
                  name: 'portfolio-generator-auto-create',
                  component: () => import('@/views/NewOrder/PortfolioGenerator/AutoCreate.vue')
                },
                {
                  path: 'order/:id(\\-?\\d+)',
                  name: 'portfolio-generator-edit-order',
                  component: () => import('@/views/NewOrder/PortfolioGenerator/EditOrder.vue'),
                  props: (route) => ({
                    id: !route.params.id ? undefined : +route.params.id
                  })
                },
                {
                  path: 'tranche/:id(\\-?\\d+)',
                  name: 'portfolio-generator-edit-tranche',
                  component: () => import('@/views/NewOrder/PortfolioGenerator/EditTranche.vue'),
                  props: (route) => ({
                    id: !route.params.id ? undefined : +route.params.id
                  })
                }
              ]
            },
            {
              path: 'confirmation/:id(-?\\d+)',
              name: 'order-confirmation',
              component: () => import('@/views/NewOrder/OrderConfirmationView.vue'),
              props: (route) => ({ id: +route.params.id })
            }
          ]
        },
        {
          path: 'order-history',
          name: 'order-history',
          redirect: { name: 'order-history-all' },
          children: [
            {
              path: 'all',
              name: 'order-history-all',
              component: () => import('@/views/OrderHistory/OrdersHistory.vue')
            },
            {
              path: ':id(-?\\d+)',
              name: 'order-history-single',
              component: () => import('@/views/OrderHistory/SingleOrderView.vue'),
              props: (route) => ({ id: +route.params.id })
            },
            {
              path: ':id(-?\\d+)/sign',
              name: 'order-history-sign',
              component: () => import('@/views/OrderHistory/ApproveOrderView.vue'),
              props: (route) => ({ id: +route.params.id })
            }
          ]
        },
        {
          path: 'communications',
          name: 'communications',
          redirect: { name: 'communications-guidelines' },
          component: () => import('@/views/Communications/CommunicationsView.vue'),
          children: [
            {
              path: 'guidelines',
              name: 'communications-guidelines',
              component: () => import('@/views/Communications/CommunicationsGuidelinesView.vue')
            },
            {
              path: 'about-klimate',
              name: 'communications-about-klimate',
              component: () => import('@/views/Communications/CommunicationsAboutKlimateView.vue')
            },
            {
              path: 'the-cdr-market',
              name: 'communications-the-cdr-market',
              component: () => import('@/views/Communications/CommunicationsTheCdrMarketView.vue')
            },
            {
              // The pages sat under /communications/guidance until March 2025 and the automated emails
              // still link there, so the old addresses keep working.
              path: 'guidance/:page?',
              redirect: (to) => ({ path: `/communications/${to.params.page || 'guidelines'}` })
            }
          ]
        },
        {
          path: 'projects',
          children: [
            {
              path: '',
              name: 'projects',
              component: () => import('@/views/Projects/ProjectsView.vue'),
              children: [
                {
                  path: 'owned', // Redirect from old /projects/owned
                  redirect: { name: 'projects' }
                },
                {
                  path: 'others', // Redirect from old /projects/others
                  redirect: { name: 'projects' }
                }
              ]
            },
            {
              path: ':id(-?\\d+)',
              name: 'project',
              props: (route) => ({ id: +route.params.id }),
              component: () => import('@/views/Projects/SinglePageProjectView.vue'),
              children: [
                {
                  path: 'engagement', // Redirect from old /projects/123/engagement
                  redirect: { name: 'project' }
                },
                {
                  path: 'description', // Redirect from old /projects/123/description
                  redirect: { name: 'project' }
                },
                {
                  path: 'dd', // Redirect from old /projects/123/dd
                  redirect: { name: 'project' }
                },
                {
                  path: 'mrv', // Redirect from old /projects/123/mrv
                  redirect: { name: 'project' }
                }
              ]
            }
          ]
        },
        {
          path: 'methods',
          children: [
            {
              path: '',
              name: 'methods',
              component: () => import('@/views/Methods/MethodsView.vue'),
              children: [
                {
                  path: 'owned', // Redirect from old /methods/owned
                  redirect: 'methods'
                },
                {
                  path: 'others', // Redirect from old /methods/owned
                  redirect: 'methods'
                }
              ]
            },
            {
              path: ':id(-?\\d+)',
              name: 'method',
              props: (route) => ({ id: +route.params.id }),
              component: () => import('@/views/Methods/MethodView.vue'),
              children: [
                {
                  path: 'engagement', // Redirect from old /methods/123/engagement
                  redirect: 'method'
                },
                {
                  path: 'description', // Redirect from old /methods/123/description
                  redirect: 'method'
                }
              ]
            }
          ]
        },
        {
          path: 'help',
          name: 'help',
          component: () => import('@/views/HelpView.vue')
        },
        {
          path: '/supplier',
          redirect: { name: 'supplier-dashboard' },
          children: [
            {
              path: 'dashboard',
              name: 'supplier-dashboard',
              component: () => import('@/views/SupplierDashboard/SupplierDashboardView.vue')
            },
            {
              path: 'project/:id(-?\\d+)',
              component: () => import('@/views/SupplierDashboard/SupplierProjectView.vue'),
              children: [
                {
                  path: '',
                  name: 'supplier-project',
                  redirect: { name: 'duediligence-climate-impact' }
                },
                {
                  path: 'preassessment',
                  name: 'preassessment',
                  redirect: { name: 'preassessment-climate-impact' },
                  component: () => import('@/views/SupplierDashboard/ProjectAssessmentView.vue'),
                  props: {
                    assessment: 'preassessment',
                    processType: FormProcessType.PreAssessment
                  },
                  children: [
                    {
                      path: 'climate-impact',
                      name: 'preassessment-climate-impact',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.ClimateImpact,
                        processType: FormProcessType.PreAssessment
                      }
                    },
                    {
                      path: 'co-benefit',
                      name: 'preassessment-co-benefit',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.CoBenefit,
                        processType: FormProcessType.PreAssessment
                      }
                    },
                    {
                      path: 'integrity',
                      name: 'preassessment-integrity',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.Integrity,
                        processType: FormProcessType.PreAssessment
                      }
                    },
                    {
                      path: 'scope-and-implementation',
                      name: 'preassessment-scope-and-implementation',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.ScopeAndImplementation,
                        processType: FormProcessType.PreAssessment
                      }
                    },
                    {
                      path: 'scoring',
                      name: 'preassessment-scoring',
                      component: () =>
                        import('@/views/SupplierDashboard/PreAssessmentScoringView.vue')
                    }
                  ]
                },
                {
                  path: 'duediligence',
                  name: 'duediligence',
                  redirect: { name: 'duediligence-climate-impact' },
                  component: () => import('@/views/SupplierDashboard/ProjectAssessmentView.vue'),
                  props: {
                    assessment: 'duediligence',
                    processType: FormProcessType.DueDiligence
                  },
                  children: [
                    {
                      path: 'climate-impact',
                      name: 'duediligence-climate-impact',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.ClimateImpact,
                        processType: FormProcessType.DueDiligence
                      }
                    },
                    {
                      path: 'co-benefit',
                      name: 'duediligence-co-benefit',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.CoBenefit,
                        processType: FormProcessType.DueDiligence
                      }
                    },
                    {
                      path: 'integrity',
                      name: 'duediligence-integrity',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.Integrity,
                        processType: FormProcessType.DueDiligence
                      }
                    },
                    {
                      path: 'scope-and-implementation',
                      name: 'duediligence-scope-and-implementation',
                      component: () => import('@/views/SupplierDashboard/DDFormView.vue'),
                      props: {
                        category: FormCategory.ScopeAndImplementation,
                        processType: FormProcessType.DueDiligence
                      }
                    },
                    {
                      path: 'scoring',
                      name: 'duediligence-scoring',
                      component: () => import('@/views/SupplierDashboard/ScoringView.vue')
                    }
                  ]
                },
                {
                  path: 'climate-impact',
                  redirect: (to) => ({
                    name: 'duediligence-climate-impact',
                    params: to.params,
                    query: to.query,
                    hash: to.hash
                  })
                },
                {
                  path: 'co-benefit',
                  redirect: (to) => ({
                    name: 'duediligence-co-benefit',
                    params: to.params,
                    query: to.query,
                    hash: to.hash
                  })
                },
                {
                  path: 'integrity',
                  redirect: (to) => ({
                    name: 'duediligence-integrity',
                    params: to.params,
                    query: to.query,
                    hash: to.hash
                  })
                },
                {
                  path: 'scope-and-implementation',
                  redirect: (to) => ({
                    name: 'duediligence-scope-and-implementation',
                    params: to.params,
                    query: to.query,
                    hash: to.hash
                  })
                },
                {
                  path: 'scoring',
                  redirect: (to) => ({
                    name: 'duediligence-scoring',
                    params: to.params,
                    query: to.query,
                    hash: to.hash
                  })
                }
              ]
            }
          ]
        }
      ]
    }
  ]
})

export default router
